// Уведомления: лента, счётчик непрочитанных и живой канал в одном месте.
//
// ## Почему канал живёт рядом с запросами, а не в компоненте
//
// Кадр канала не несёт данных — он говорит «сходи за новым». Значит его единственное действие
// это пометить кэш устаревшим, а кэш живёт здесь. Компонент, который сам открывал бы сокет и
// сам решал, что обновить, повторил бы это правило в каждом экране, и один из них однажды
// забыл бы обновиться.
//
// ## Что обновляется по кадру
//
// Лента уведомлений и очереди, куда человек пойдёт по уведомлению: заявки на выдачу и очередь
// приёмки отчётов. Обновление именно кэша, а не экрана: если человек сейчас смотрит другое,
// он ничего не увидит и не потеряет — данные подтянутся, когда он придёт.

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { useAuthStore } from '@/shared/auth/store';
import type { NotificationFeed, ReadResult } from '@/shared/api/endpoints/notifications';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationsRead,
} from '@/shared/api/endpoints/notifications';
import { openRealtimeChannel } from '@/shared/realtime/channel';

export const notificationKeys = {
  все: ['notifications'] as const,
  лента: (onlyUnread: boolean) => ['notifications', 'feed', onlyUnread] as const,
};

/** Ключи, которые обновляются вместе с лентой: туда человек пойдёт по уведомлению. */
const СВЯЗАННЫЕ_КЛЮЧИ: readonly (readonly string[])[] = [
  ['accountable'],
  ['cash-requests'],
  // «Дела» — первый экран рабочего дня, и он же дольше всех остаётся открытым: человек
  // держит его на втором мониторе и возвращается глазами. Без этой строки поданный чек
  // появлялся бы в деле через минуту страховочного интервала, а в очереди приёмки — сразу,
  // и два числа на соседних экранах расходились бы у него на глазах.
  ['cases'],
];

export function useNotificationFeed(onlyUnread = false): UseQueryResult<NotificationFeed> {
  return useQuery({
    queryKey: notificationKeys.лента(onlyUnread),
    queryFn: () => fetchNotifications({ only_unread: onlyUnread, limit: 50 }),
    // Канал даёт мгновенность, а этот интервал — страховку на случай, когда канал не поднялся
    // (старый прокси, корпоративная сеть, спящий ноутбук). Минута выбрана как «человек не
    // заметит, что это не мгновенно», а не как частота опроса ради частоты.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useUnreadCount(): number {
  const { data } = useNotificationFeed(false);
  return data?.unread ?? 0;
}

/**
 * Пометка прочитанным применяется СРАЗУ, до ответа сервера.
 *
 * Это тот же случай, что «своя строка в очереди приёмки» из правила мгновенного отклика:
 * счётчик считается по собственным уведомлениям человека, и сколько из них были
 * непрочитанными, клиент знает точно. Ждать ответа значило бы показывать прежнее число ещё
 * пару секунд после нажатия — а человек в этот момент уже ушёл в раздел по ссылке и решил,
 * что нажатие не сработало.
 *
 * Ошибка возвращает всё как было: снимок снимается ДО правки, обратное действие не
 * вычисляется.
 */
function пометить(данные: NotificationFeed | undefined, ids: readonly string[] | 'все') {
  if (данные === undefined) {
    return данные;
  }
  const момент = new Date().toISOString();
  let погашено = 0;
  const items = данные.items.map((item) => {
    const нужно = ids === 'все' || ids.includes(item.id);
    if (!нужно || (item.read_at !== null && item.read_at !== undefined)) {
      return item;
    }
    погашено += 1;
    return { ...item, read_at: момент };
  });
  return { ...данные, items, unread: Math.max(0, данные.unread - погашено) };
}

function useПометка<Аргумент>(
  запрос: (аргумент: Аргумент) => Promise<ReadResult>,
  какие: (аргумент: Аргумент) => readonly string[] | 'все',
): (аргумент: Аргумент) => Promise<ReadResult> {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: запрос,
    onMutate: async (аргумент: Аргумент) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.все });
      const снимок = queryClient.getQueriesData<NotificationFeed>({
        queryKey: notificationKeys.все,
      });
      queryClient.setQueriesData<NotificationFeed>({ queryKey: notificationKeys.все }, (было) =>
        пометить(было, какие(аргумент)),
      );
      return { снимок };
    },
    onError: (_ошибка, _аргумент, контекст) => {
      // Вернуть ровно то, что было: пересчитывать «обратное действие» нельзя, за время
      // запроса ленту мог обновить живой канал.
      for (const [ключ, значение] of контекст?.снимок ?? []) {
        queryClient.setQueryData(ключ, значение);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.все });
    },
  });
  return (аргумент: Аргумент) => mutation.mutateAsync(аргумент);
}

export function useMarkRead(): (ids: string[]) => Promise<ReadResult> {
  return useПометка(
    (ids: string[]) => markNotificationsRead(ids),
    (ids) => ids,
  );
}

export function useMarkAllRead(): () => Promise<ReadResult> {
  const пометка = useПометка(
    () => markAllNotificationsRead(),
    () => 'все' as const,
  );
  return () => пометка(undefined);
}

/**
 * Поднять живой канал на весь сеанс. Вызывается один раз в каркасе защищённой области.
 *
 * Возврат ничего не значит: канал — ускоритель. Он не поднялся — человек увидит новое через
 * страховочный интервал ленты, а не никогда.
 */
export function useRealtimeChannel(): void {
  const queryClient = useQueryClient();
  const вошёл = useAuthStore((state) => state.accessToken !== null);

  useEffect(() => {
    if (!вошёл) {
      return;
    }
    const канал = openRealtimeChannel(() => useAuthStore.getState().accessToken, {
      onNews: () => {
        void queryClient.invalidateQueries({ queryKey: notificationKeys.все });
        for (const ключ of СВЯЗАННЫЕ_КЛЮЧИ) {
          void queryClient.invalidateQueries({ queryKey: ключ });
        }
      },
    });
    return () => канал.close();
  }, [вошёл, queryClient]);
}
